import _ from 'lodash';
import { storageFileUrl } from '@/utils/fileAccessUrl';
import { proxiedFileUrl } from '@/extensions/generative-media/imageThumbnail';
import { getFitStringFromProcessObject } from '@/helpers';

// A browser-displayable URL that shows the header image's EXACT crop — the same on-the-fly
// FileController crop the rest of the app relies on (BusinessCard's businessCardImageCroppedSrc,
// ImageContent): the storage route (token-authenticated for a private source) plus the
// imgix-style fit querystring. FileController renders the crop for a private File too
// (getPrivateResource, non-Image + fit branch), so NO server-side bake is needed for the
// preview. Falls back to a baked variant / the source url when there is no crop yet.
export const headerPreviewUrl = (image) => {
    if (!image) return '';

    // A baked variant already carries the crop. Serve it through the token-authenticated
    // proxy route (like the editor canvas / library thumbnails), NOT its raw CDN url: a
    // private variant's CDN url 403s in an <img> (DigitalOcean ignores object ACLs).
    const processedFile = _.get(image, 'processedFile');
    const variantUrl = proxiedFileUrl(processedFile) || _.get(processedFile, 'url');
    if (variantUrl) return variantUrl;

    // The source File the crop applies to (served, with the crop, by FileController).
    const file = {
        id: _.get(image, 'source.id'),
        name: image.name || _.get(image, 'source.name'),
        visibility: image.visibility,
        url: _.get(image, 'source.url') || image.url,
    };
    const base = storageFileUrl(file);
    if (!base) return _.get(image, 'signed_url') || file.url || '';

    const processing = _.get(image, 'settings.processing');
    if (!processing) return base;

    const fit = getFitStringFromProcessObject(processing);
    return fit ? `${base}${base.indexOf('?') === -1 ? '?' : '&'}${fit}` : base;
};

// Reduce the interactive builder's header image (which carries display-only extras like
// signed_url / visibility / processedFile) to the clean fields the GraphQL ImageInput type
// accepts — the same shape a BusinessCard image is saved with. The backend persists it as a
// standalone Image and bakes the admin's 4:1 crop (settings.processing) into a servable file.
// Returns null when there is no header image.
export const sanitizeHeaderImageInput = (image) => {
    if (!image) return null;

    const sourceId = _.get(image, 'source.id');

    return {
        id: image.id,
        type: image.type || 'communication-header-image',
        upload: image.upload || false,
        // The ONLY nested object here, and the one field not picked apart: Apollo
        // stamps __typename onto it, which ImageSettingsInput rejects and which
        // failed the whole settings-tab save for any shop whose auto-reply has a
        // header image. Every sibling is listed field by field, which is why none
        // of them ever leaked one.
        settings: image.settings ? _.removeDeepByKey(_.cloneDeep(image.settings), '__typename') : null,
        // Re-cropping a saved header bakes the crop into a `variant:` file and clears
        // settings.processing; without the pointer the save fell back to the default crop.
        processing_query_string: image.processing_query_string || null,
        alt: image.alt || null,
        width: image.width || null,
        height: image.height || null,
        // Keep source so the backend can attach a library selection (new:file-library:).
        source: sourceId ? { id: parseInt(sourceId, 10) || sourceId } : null,
    };
};
